
import React, { useState, useEffect, useMemo } from 'react';
import RecipeCard from './RecipeCard';
import PlusIcon from './icons/PlusIcon';
import CartIcon from './icons/CartIcon';
import TrashIcon from './icons/TrashIcon';
import PencilIcon from './icons/PencilIcon';
import LayoutGridIcon from './icons/LayoutGridIcon';
import ListIcon from './icons/ListIcon';
import type { Recipe, ShoppingCart, Role, Household, RecipeCategory, MealType, MealPlan, PlannedMeal, HouseholdRole, User, DayOfWeek, Language, ActiveShoppingCart, ActiveCartItem, Chef, UserMealPreferences } from '../types';
import type { ShoppingListType } from '../types';
import XIcon from './icons/XIcon';
import SearchField from './SearchField';
import UsersIcon from './icons/UsersIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import CalendarPlusIcon from './icons/CalendarPlusIcon';
import HouseholdIcon from './icons/HouseholdIcon';
import ChefHatIcon from './icons/ChefHatIcon';
import StarIcon from './icons/StarIcon';
import ClockIcon from './icons/ClockIcon';
import FilterIcon from './icons/FilterIcon';
import CheckSquareIcon from './icons/CheckSquareIcon';
import HeartIcon from './icons/HeartIcon';
import InfoIcon from './icons/InfoIcon';
import RecipeFilterModal, { type FilterState } from './RecipeFilterModal';
import RecipeWalkthroughModal from './RecipeWalkthroughModal';
import { getActiveChefs } from '../utils/chefDisplay';
import { isFavoriteRecipe } from '../utils/userMealPreferences';
import { filterAndSortRecipes, countActiveRecipeFilters, type RecipeVisibilityChip } from '../utils/recipeFilters';
import type { KnownAllergyId } from '../types';
import ConfirmationModal from './ConfirmationModal';
import { DAYS_OF_WEEK, DAY_TRANSLATIONS } from '../types';
import { getTranslation, isRTL } from '../utils/translations';
import { detectCategory } from '../utils/unitConversion';
import { getDefaultScheduleSlot } from '../utils/mealScheduleDefaults';

interface RecipeManagerProps {
    recipes: Recipe[];
    activeCart: ActiveShoppingCart | null;
    onUpdateActiveCart: (cart: ActiveShoppingCart) => Promise<void>;
    role: Role;
    householdRole: HouseholdRole;
    onAddRecipe: () => void;
    onDeleteRecipe: (recipeId: string) => void;
    onEditRecipe: (recipeId: string) => void;
    onViewRecipe: (recipe: Recipe) => void;
    onScheduleRecipe: (recipe: Recipe) => void;
    onAddRecipesToDay: (recipeIds: string[], day: DayOfWeek, mealType: MealType) => void;
    currentUserId: string;
    activeHouseholdId?: string;
    allUsers?: User[];
    chefs?: Chef[];
    userMealPreferences?: UserMealPreferences | null;
    onToggleFavoriteRecipe?: (recipeId: string) => Promise<void>;
    visibleMealTypes?: MealType[];
    language?: Language;
    orderedDays?: DayOfWeek[];
    pendingChefFilterId?: string | null;
    onPendingChefFilterApplied?: () => void;
    activeHousehold?: Household | null;
}

type SortKey = 'recent' | 'rated' | 'name' | 'prepTime';

const RecipeManager: React.FC<RecipeManagerProps> = ({ 
    recipes, activeCart, onUpdateActiveCart, role, householdRole, onAddRecipe, onDeleteRecipe, 
    onEditRecipe, onViewRecipe, onScheduleRecipe, onAddRecipesToDay,
    currentUserId, activeHouseholdId, allUsers, chefs = [], userMealPreferences = null, onToggleFavoriteRecipe,
    visibleMealTypes = ['Breakfast', 'Lunch', 'Dinner'], language, orderedDays,
    pendingChefFilterId = null, onPendingChefFilterApplied,
    activeHousehold = null,
}) => {
    const canManage = role === 'superuser' || ['owner', 'admin', 'editor'].includes(householdRole);
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);
    
    const [displayMode, setDisplayMode] = useState<'grid' | 'list'>('grid');
    const [isMobile, setIsMobile] = useState(false);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isWalkthroughOpen, setIsWalkthroughOpen] = useState(false);
    
    // Deletion State
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

    const defaultScheduleSlot = useMemo(
        () => getDefaultScheduleSlot(visibleMealTypes),
        [visibleMealTypes],
    );

    // Selection actions
    const [isSchedulePickerOpen, setIsSchedulePickerOpen] = useState(false);
    const [scheduleDay, setScheduleDay] = useState<DayOfWeek>(defaultScheduleSlot.day);
    const [scheduleMeal, setScheduleMeal] = useState<MealType>(defaultScheduleSlot.mealType);
    const [isAddingToCart, setIsAddingToCart] = useState(false);

    // Filter States
    const [categoryFilters, setCategoryFilters] = useState<RecipeCategory[]>([]);
    const [dietTypeFilters, setDietTypeFilters] = useState<(keyof import('../types').DietTypes)[]>([]);
    const [lifestyleTagFilters, setLifestyleTagFilters] = useState<(keyof import('../types').LifestyleTags)[]>([]);
    const [mealTypeFilters, setMealTypeFilters] = useState<MealType[]>([]);
    const [visibilitySelections, setVisibilitySelections] = useState<RecipeVisibilityChip[]>([]);
    const [excludeAllergens, setExcludeAllergens] = useState<KnownAllergyId[]>([]);
    const [chefFilters, setChefFilters] = useState<string[]>([]);
    const [minRatingFilter, setMinRatingFilter] = useState<number | null>(null);

    // Sort States
    const [sortBy, setSortBy] = useState<SortKey>('recent');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
            if (window.innerWidth < 768) setDisplayMode('list');
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!pendingChefFilterId) return;
        setChefFilters([pendingChefFilterId]);
        onPendingChefFilterApplied?.();
    }, [pendingChefFilterId, onPendingChefFilterApplied]);

    const filterState = useMemo<FilterState>(() => ({
        categories: categoryFilters,
        dietTypes: dietTypeFilters,
        lifestyleTags: lifestyleTagFilters,
        mealTypes: mealTypeFilters,
        visibilitySelections,
        excludeAllergens,
        sortBy,
        sortOrder,
        chefIds: chefFilters,
        minRating: minRatingFilter,
    }), [categoryFilters, dietTypeFilters, lifestyleTagFilters, mealTypeFilters, visibilitySelections, excludeAllergens, sortBy, sortOrder, chefFilters, minRatingFilter]);

    const activeFilterCount = useMemo(() => countActiveRecipeFilters(filterState), [filterState]);

    const processedItems = useMemo(() => filterAndSortRecipes(recipes, filterState, {
        searchQuery,
        currentUserId,
        activeHouseholdId,
        userMealPreferences,
        chefs,
        allUsers,
    }), [recipes, filterState, searchQuery, currentUserId, activeHouseholdId, userMealPreferences, chefs, allUsers]);

    const selectedRecipes = useMemo(
        () => processedItems.filter(r => selectedIds.has(r.id)),
        [processedItems, selectedIds]
    );

    const deletableSelected = useMemo(
        () => selectedRecipes.filter(r => role === 'superuser' || (canManage && r.householdId === activeHouseholdId)),
        [selectedRecipes, role, canManage, activeHouseholdId]
    );

    const exitSelectionMode = () => {
        setSelectionMode(false);
        setSelectedIds(new Set());
    };

    const handleToggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleAddSelectedToCart = async () => {
        if (!activeCart || selectedRecipes.length === 0) return;
        setIsAddingToCart(true);
        try {
            const nextItems = [...activeCart.items];
            for (const recipe of selectedRecipes) {
                for (const ing of recipe.ingredients) {
                    const nameClean = ing.name.trim();
                    if (!nameClean) continue;
                    const itemId = `${nameClean.toLowerCase()}_${ing.unit.toLowerCase().trim()}`;
                    const existingIndex = nextItems.findIndex(i => i.id === itemId);
                    const newSource = {
                        id: `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                        originName: recipe.name,
                        quantity: ing.quantity,
                        originType: 'recipe' as const,
                    };
                    if (existingIndex > -1) {
                        nextItems[existingIndex] = {
                            ...nextItems[existingIndex],
                            done: false,
                            sources: [...nextItems[existingIndex].sources, newSource],
                        };
                    } else {
                        nextItems.push({
                            id: itemId,
                            name: nameClean,
                            unit: ing.unit,
                            isMeat: ing.isMeat,
                            done: false,
                            category: detectCategory(ing.name, ing.isMeat),
                            sources: [newSource],
                        });
                    }
                }
            }
            await onUpdateActiveCart({ ...activeCart, items: nextItems });
            exitSelectionMode();
        } finally {
            setIsAddingToCart(false);
        }
    };

    const openSchedulePicker = () => {
        const { day, mealType } = getDefaultScheduleSlot(visibleMealTypes);
        setScheduleDay(day);
        setScheduleMeal(mealType);
        setIsSchedulePickerOpen(true);
    };

    const handleScheduleSelected = () => {
        if (selectedIds.size === 0) return;
        onAddRecipesToDay(Array.from(selectedIds), scheduleDay, scheduleMeal);
        setIsSchedulePickerOpen(false);
        exitSelectionMode();
    };

    const handleBulkDelete = () => {
        deletableSelected.forEach(r => onDeleteRecipe(r.id));
        setShowBulkDeleteConfirm(false);
        exitSelectionMode();
    };

    const favoritesOnly = visibilitySelections.includes('favorites');

    const toggleFavoritesView = () => {
        setVisibilitySelections((prev) =>
            prev.includes('favorites')
                ? prev.filter((item) => item !== 'favorites')
                : [...prev, 'favorites'],
        );
    };

    const containerClasses = displayMode === 'list' 
        ? "flex flex-col p-2 space-y-2"
        : "grid grid-cols-2 sm:grid-cols-3 gap-2 p-2";

    return (
        <div className="flex flex-col h-full bg-white dark:bg-slate-900 animate-fade-in relative transition-colors duration-200">
            {/* Header: Search & Action Bar */}
            <div className="flex-shrink-0 z-30 bg-white dark:bg-slate-900 p-4 pb-2 space-y-3 border-b border-slate-50 dark:border-slate-800 shadow-sm transition-colors duration-200">
                
                {/* Top Row: Search & Add */}
                <div className="flex gap-3">
                    <SearchField
                        className="flex-grow"
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={t('search')}
                        clearLabel={t('clearSearch')}
                    />
                    {canManage && (
                        <button
                            id="recipe-add-btn"
                            onClick={onAddRecipe}
                            className="p-3.5 bg-brand-orange text-white rounded-2xl shadow-lg shadow-brand-orange/20 active:scale-95 transition-all flex-shrink-0"
                        >
                            <PlusIcon className="h-6 w-6" />
                        </button>
                    )}
                </div>

                {/* Control Bar: View Toggle, Sort, Filter */}
                <div className="flex items-center justify-between">
                    {/* Left: View Toggle and Info */}
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl gap-0.5">
                            <button onClick={() => setDisplayMode('grid')} className={`p-1.5 rounded-lg transition-all ${displayMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-brand-sage' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}><LayoutGridIcon className="w-4 h-4" /></button>
                            <button onClick={() => setDisplayMode('list')} className={`p-1.5 rounded-lg transition-all ${displayMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-brand-sage' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}><ListIcon className="w-4 h-4" /></button>
                        </div>
                        <button 
                            onClick={() => setIsWalkthroughOpen(true)}
                            className="p-2 text-slate-400 hover:text-brand-sage bg-slate-100 dark:bg-slate-800 rounded-full transition-colors flex-shrink-0"
                            title={t('tutorial')}
                        >
                            <InfoIcon className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2">
                        {/* Favorites quick toggle (Icon Only) */}
                        <button 
                            id="recipe-favorites-btn"
                            onClick={toggleFavoritesView}
                            className={`p-2 rounded-full border shadow-sm transition-all flex items-center justify-center flex-shrink-0
                                ${favoritesOnly
                                    ? 'border-brand-sage text-brand-sage bg-brand-sage/10 dark:bg-brand-sage/20'
                                    : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                            title={t('showFavorites')}
                        >
                            <HeartIcon className={`w-5 h-5 ${favoritesOnly ? 'text-brand-sage' : 'text-gray-600 dark:text-slate-300'}`} filled={favoritesOnly} />
                        </button>

                        {/* Filter Button (Pill Style) */}
                        <button 
                            id="recipe-filter-btn"
                            onClick={() => setIsFilterModalOpen(true)}
                            className={`
                                relative flex flex-row items-center justify-center gap-2 h-9 min-w-9 sm:min-w-0 px-2 sm:px-4 rounded-full border shadow-sm transition-all flex-shrink-0
                                ${activeFilterCount > 0 
                                    ? 'border-brand-sage text-brand-sage bg-brand-sage/5 dark:bg-brand-sage/20' 
                                    : 'border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'}
                            `}
                            title={t('filter')}
                        >
                            <FilterIcon className={`w-4 h-4 shrink-0 ${activeFilterCount > 0 ? 'text-brand-sage' : 'text-gray-600 dark:text-slate-400'}`} />
                            <span className="hidden sm:inline text-sm font-medium">{t('filter')}</span>
                            {activeFilterCount > 0 && (
                                <span className="absolute -top-1 end-0 sm:static min-w-[1rem] h-4 px-0.5 bg-brand-sage text-white text-[9px] flex items-center justify-center rounded-full font-bold shrink-0 leading-none">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>

                        {/* Selection Mode Toggle */}
                        <button
                            id="recipe-select-btn"
                            onClick={() => { setSelectionMode(!selectionMode); setSelectedIds(new Set()); }}
                            className={`
                                flex flex-row items-center justify-center gap-2 h-9 w-9 sm:w-auto px-0 sm:px-4 rounded-full border shadow-sm transition-all flex-shrink-0 ltr:ml-2 rtl:mr-2
                                ${selectionMode 
                                    ? 'bg-slate-900 dark:bg-slate-700 border-slate-900 dark:border-slate-700 text-white hover:bg-slate-800 dark:hover:bg-slate-600' 
                                    : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'}
                            `}
                            title={selectionMode ? t('cancel') : t('select')}
                        >
                            <CheckSquareIcon className="w-4 h-4 shrink-0" />
                            <span className="hidden sm:inline text-sm font-medium">
                                {selectionMode ? t('cancel') : t('select')}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Scrollable Results */}
            <div className={`flex-grow overflow-y-auto scrollbar-none ${selectionMode ? 'pb-56' : 'pb-32'}`}>
                <div className={containerClasses}>
                    {processedItems.length > 0 ? (
                        processedItems.map(item => (
                            <div key={item.id} className="w-full h-full">
                                <RecipeCard 
                                    recipe={item} role={role} canManage={role === 'superuser' || (canManage && item.householdId === activeHouseholdId)}
                                    onEdit={() => onEditRecipe(item.id)}
                                    onView={() => onViewRecipe(item)} isReorderMode={false} onReorder={() => {}}
                                    isSelectionMode={selectionMode} isSelected={selectedIds.has(item.id)}
                                    onToggleSelect={handleToggleSelect} currentUserId={currentUserId}
                                    showPrivateBadge={true} 
                                    onSchedule={() => onScheduleRecipe(item)}
                                    language={language} isMobile={isMobile} allUsers={allUsers} activeHouseholdId={activeHouseholdId}
                                    layout={displayMode}
                                    isFavorite={isFavoriteRecipe(userMealPreferences, item.id)}
                                    onToggleFavorite={onToggleFavoriteRecipe ? () => onToggleFavoriteRecipe(item.id) : undefined}
                                />
                            </div>
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 opacity-30 w-full col-span-full">
                            <ChefHatIcon className="w-16 h-16 mb-4 text-slate-300 dark:text-slate-600" />
                            <p className="font-black text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">{t('noResults')}</p>
                        </div>
                    )}
                </div>
            </div>

            <RecipeFilterModal 
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                currentFilters={filterState}
                onApply={(newFilters: FilterState) => {
                    setCategoryFilters(newFilters.categories);
                    setDietTypeFilters(newFilters.dietTypes);
                    setLifestyleTagFilters(newFilters.lifestyleTags);
                    setMealTypeFilters(newFilters.mealTypes);
                    setVisibilitySelections(newFilters.visibilitySelections);
                    setExcludeAllergens(newFilters.excludeAllergens);
                    setSortBy(newFilters.sortBy);
                    setSortOrder(newFilters.sortOrder);
                    setChefFilters(newFilters.chefIds);
                    setMinRatingFilter(newFilters.minRating);
                }}
                language={language}
                chefs={getActiveChefs(chefs)}
                recipes={recipes}
                allUsers={allUsers}
                activeHousehold={activeHousehold}
            />

            <ConfirmationModal
                isOpen={showBulkDeleteConfirm}
                title={t('deleteRecipe')}
                message={`${t('delete')} ${deletableSelected.length} ${t('recipes')}?`}
                confirmText={t('delete')}
                cancelText={t('cancel')}
                onConfirm={handleBulkDelete}
                onCancel={() => setShowBulkDeleteConfirm(false)}
            />

            {isSchedulePickerOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                            <h3 className="font-black text-sm uppercase tracking-widest text-slate-800 dark:text-slate-100">
                                {t('scheduleInMenu')}
                            </h3>
                            <button
                                onClick={() => setIsSchedulePickerOpen(false)}
                                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl"
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-5 space-y-5">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{t('day')}</p>
                                <div className="flex flex-wrap gap-2">
                                    {(orderedDays || DAYS_OF_WEEK).map(day => (
                                        <button
                                            key={day}
                                            onClick={() => setScheduleDay(day)}
                                            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                                                scheduleDay === day
                                                    ? 'bg-brand-sage text-white shadow-md'
                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                                            }`}
                                        >
                                            {t(DAY_TRANSLATIONS[day])}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{t('meal')}</p>
                                <div className="flex flex-wrap gap-2 justify-start">
                                    {visibleMealTypes.map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setScheduleMeal(type)}
                                            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                                                scheduleMeal === type
                                                    ? 'bg-brand-sage text-white shadow-md'
                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                                            }`}
                                        >
                                            {t(type === 'Breakfast' ? 'morning' : type === 'Lunch' ? 'noon' : 'evening')}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="p-5 border-t border-slate-100 dark:border-slate-800 flex gap-3">
                            <button
                                onClick={() => setIsSchedulePickerOpen(false)}
                                className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl font-black text-xs uppercase tracking-widest"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handleScheduleSelected}
                                className="flex-[2] py-3 bg-brand-sage text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2"
                            >
                                <CalendarPlusIcon className="w-4 h-4" />
                                {t('scheduleInMenu')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectionMode && (
                <div className="absolute bottom-24 left-3 right-3 z-50 animate-fade-in pointer-events-none">
                    <div className="pointer-events-auto bg-slate-900 dark:bg-slate-800 text-white rounded-2xl shadow-2xl border border-slate-700 p-3 space-y-2.5 max-w-2xl mx-auto">
                        <div className="flex items-center justify-between gap-3 px-1">
                            <span className="font-bold text-sm">
                                {selectedIds.size} {t('selected')}
                            </span>
                            <button
                                onClick={exitSelectionMode}
                                className="px-3 py-1.5 text-slate-400 font-bold text-[10px] uppercase tracking-widest hover:text-white transition-colors"
                            >
                                {t('cancel')}
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={openSchedulePicker}
                                disabled={selectedIds.size === 0}
                                className="py-3 bg-brand-sage text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg disabled:opacity-30 flex items-center justify-center gap-1.5 min-w-0"
                            >
                                <CalendarPlusIcon className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{t('scheduleInMenu')}</span>
                            </button>
                            <button
                                onClick={handleAddSelectedToCart}
                                disabled={isAddingToCart || selectedIds.size === 0 || !activeCart}
                                className="py-3 bg-brand-orange text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-brand-orange/20 disabled:opacity-30 flex items-center justify-center gap-1.5 min-w-0"
                            >
                                {isAddingToCart ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <CartIcon className="w-4 h-4 flex-shrink-0" />
                                )}
                                <span className="truncate">{t('addToActive')}</span>
                            </button>
                            {deletableSelected.length > 0 && (
                                <button
                                    onClick={() => setShowBulkDeleteConfirm(true)}
                                    disabled={selectedIds.size === 0}
                                    className="col-span-2 py-2.5 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg disabled:opacity-30 flex items-center justify-center gap-1.5 min-w-0"
                                >
                                    <TrashIcon className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">{t('delete')}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            <RecipeWalkthroughModal
                isOpen={isWalkthroughOpen}
                onClose={() => setIsWalkthroughOpen(false)}
                language={language}
            />
        </div>
    );
};

export default RecipeManager;
